import { useEffect, useState } from "react";
import { api, fmtPct, fmtUsd } from "@/lib/api";
import { Minus, CheckCircle2, XCircle, Trophy, TrendingDown, Target, Activity, History, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { OutlookPill } from "@/components/SignalReport";
import { Locked } from "@/components/Locked";
import { Hint, HintLabel } from "@/components/Hint";
import { PredictionLedger } from "@/components/PredictionLedger";
import { PredictionScore } from "@/components/PredictionScore";

const STATUS_STYLE = {
  open:        { label: "OPEN",        color: "#06B6D4", bg: "rgba(6,182,212,0.12)",  border: "rgba(6,182,212,0.4)" },
  completed:   { label: "COMPLETED",   color: "#94A3B8", bg: "rgba(148,163,184,0.12)", border: "rgba(148,163,184,0.4)" },
  invalidated: { label: "INVALIDATED", color: "#EF4444", bg: "rgba(239,68,68,0.14)",  border: "rgba(239,68,68,0.45)" },
  replaced:    { label: "REPLACED",    color: "#F59E0B", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.open;
  return (
    <span
      data-testid={`status-${status}`}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono-ticker font-bold tracking-widest"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}
    >
      {s.label}
    </span>
  );
};

const relTime = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const MomentumCell = ({ s }) => {
  if (!s.conditions_total) return <span className="text-xs text-slate-600" data-testid={`momentum-na-${s.id}`}>—</span>;
  const up = s.last_flip_type === "met";
  return (
    <div className="min-w-[120px]" data-testid={`momentum-${s.id}`}>
      <div className="flex items-center gap-1.5 text-xs">
        <span className="font-mono-ticker text-slate-200" data-testid={`momentum-met-${s.id}`}>{s.met_count}/{s.conditions_total}</span>
        <span className="text-slate-500">met</span>
        <span
          className={`ml-auto font-mono-ticker text-[10px] px-1.5 py-0.5 rounded ${s.change_count ? "bg-cyan-500/10 text-cyan-300" : "bg-slate-800 text-slate-500"}`}
          data-testid={`momentum-changes-${s.id}`}
          title={`${s.met_flips} flipped to met`}
        >
          {s.change_count} {s.change_count === 1 ? "change" : "changes"}
        </span>
      </div>
      <div className="h-1 bg-slate-900 rounded-full overflow-hidden border border-slate-800 mt-1">
        <div className="h-full" style={{ width: `${(s.met_count / s.conditions_total) * 100}%`, background: "linear-gradient(90deg,#8B5CF6,#06B6D4)" }} />
      </div>
      <div className="text-[10px] text-slate-500 mt-1 truncate" data-testid={`momentum-last-${s.id}`} title={s.last_flip_label || ""}>
        {s.last_flip_at ? (
          <><span className={up ? "text-emerald-400" : "text-amber-400"}>{up ? "▲ met" : "▼ unmet"}</span> · {relTime(s.last_flip_at)}</>
        ) : "no flips yet"}
      </div>
    </div>
  );
};



const Stat = ({ k, label, value, accent, hint, icon: Icon }) => (
  <div className="apx-card p-4">
    <div className="flex items-center justify-between mb-1">
      <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500"><HintLabel k={k}>{label}</HintLabel></div>
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-500" />}
    </div>
    <div className={`font-mono-ticker text-lg md:text-xl font-semibold ${accent || "text-slate-100"}`}>{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
  </div>
);

const HorizonBlock = ({ label, s }) => (
  <div className="apx-card p-4" data-testid={`horizon-${label.replace(/\s/g,'-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="font-display font-semibold">{label}</div>
      <span className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{s?.count || 0} signals</span>
    </div>
    {!s || !s.count ? (
      <div className="text-xs text-slate-500 italic">
        Not enough aged signals yet — generate signals and come back after they mature.
      </div>
    ) : (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <Metric k="win_rate" label="Win Rate" v={s.win_rate + "%"} c="text-emerald-400" />
        <Metric label="Loss Rate" v={s.loss_rate + "%"} c="text-red-400" />
        <Metric k="avg_return" label="Avg Return" v={fmtPct(s.avg_return)} c={s.avg_return >= 0 ? "text-emerald-400" : "text-red-400"} />
        <Metric label="Avg Win" v={fmtPct(s.avg_win)} c="text-emerald-300" />
        <Metric label="Avg Loss" v={fmtPct(s.avg_loss)} c="text-red-300" />
        <Metric label="Best" v={fmtPct(s.best)} c="text-emerald-200" />
        <Metric label="Worst" v={fmtPct(s.worst)} c="text-red-200" />
        <Metric k="max_drawdown" label="Max Drawdown" v={fmtPct(s.max_drawdown)} c="text-amber-300" />
        <Metric label="Wins / Losses" v={`${s.wins} / ${s.losses}`} />
      </div>
    )}
  </div>
);

const Metric = ({ k, label, v, c }) => (
  <div>
    <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{k ? <HintLabel k={k}>{label}</HintLabel> : label}</div>
    <div className={`font-mono-ticker font-semibold ${c || "text-slate-100"}`}>{v}</div>
  </div>
);

export default function TrackRecord() {
  return (
    <>
      <div className="p-4 md:p-6 pb-0 max-w-[1500px] mx-auto" data-testid="track-record-score-section">
        <PredictionScore />
      </div>
      <Locked feature="track_record" title="AI Track Record is a Pro feature"
        desc="See every past BUY / WAIT / AVOID call with its outcome at 24h, 7d and 30d, momentum columns and the full setup timeline. Your existing history is preserved and unlocks instantly on Pro.">
        <TrackRecordInner />
      </Locked>
      <div className="p-4 md:p-6 pt-0 max-w-[1500px] mx-auto" data-testid="ledger-section">
        <PredictionLedger />
      </div>
    </>
  );
}

export function TrackRecordInner() {
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState("30d");
  const [coinFilter, setCoinFilter] = useState("all");
  const [outlookFilter, setOutlookFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/signals/history", { params: { limit: 200 } }).then((r) => setSignals(r.data)),
      api.get("/signals/stats").then((r) => setStats(r.data)).catch(() => setStats(null)),
    ]).finally(() => setLoading(false));
  }, []);

  const coinOptions = Array.from(new Set(signals.map((s) => s.coin_id))).map((cid) => {
    const s = signals.find((x) => x.coin_id === cid);
    return { id: cid, name: s?.name, symbol: s?.symbol };
  });

  const filtered = signals.filter((s) => {
    if (coinFilter !== "all" && s.coin_id !== coinFilter) return false;
    if (outlookFilter !== "all" && (s.outlook || s.stance) !== outlookFilter) return false;
    if (statusFilter !== "all" && (s.status || "open") !== statusFilter) return false;
    return true;
  });

  const statusCounts = signals.reduce((acc, s) => {
    const st = s.status || "open";
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  const corrected = signals.filter((s) => s.outcome === "correct").length;
  const incorrect = signals.filter((s) => s.outcome === "incorrect").length;
  const totalWithOutcome = corrected + incorrect;
  const hitRate = totalWithOutcome ? ((corrected / totalWithOutcome) * 100).toFixed(1) + "%" : "—";

  const overall = stats?.overall?.[horizon];

  return (
    <div className="p-4 md:p-6 max-w-[1500px] mx-auto space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">AI Track Record</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Every historical EV-IDEX signal is stored with its price, outlook, strength and risk at creation. Returns below are computed from CoinGecko price data — the AI never fabricates them.
          </p>
        </div>
        <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800">
          {["7d", "30d", "90d"].map((h) => (
            <Hint key={h} k="horizon" asChild>
            <button
              data-testid={`horizon-pick-${h}`}
              onClick={() => setHorizon(h)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono-ticker ${
                horizon === h ? "bg-cyan-500/15 text-cyan-300" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {h.toUpperCase()}
            </button>
            </Hint>
          ))}
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat k="total_signals" label="Total Signals" value={signals.length} icon={History} />
        <Stat k="aged_signals" label="Aged Signals" value={stats?.actionable_signals ?? 0} icon={Activity} hint="Old enough to measure" />
        <Stat k="hit_rate" label="Simple Hit Rate" value={hitRate} icon={Target} hint={`${corrected}✓ / ${incorrect}✗ vs now`} />
        <Stat
          k="avg_return"
          label={`Avg Return · ${horizon.toUpperCase()}`}
          value={overall ? fmtPct(overall.avg_return) : "—"}
          icon={overall?.avg_return >= 0 ? Trophy : TrendingDown}
          accent={overall?.avg_return >= 0 ? "text-emerald-400" : "text-red-400"}
          hint={overall?.count ? `${overall.count} signals` : "Need aged data"}
        />
      </div>

      {/* Lifecycle status summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="status-summary">
        {(["open", "completed", "invalidated", "replaced"]).map((st) => {
          const s = STATUS_STYLE[st];
          return (
            <Hint key={st} k={`status_${st}`} asChild>
            <button
              data-testid={`status-filter-${st}`}
              onClick={() => setStatusFilter(statusFilter === st ? "all" : st)}
              className={`apx-card p-4 text-left transition-colors ${statusFilter === st ? "" : ""}`}
              style={{ borderColor: statusFilter === st ? s.border : undefined }}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-mono-ticker uppercase tracking-widest" style={{ color: s.color }}>{s.label}</div>
              </div>
              <div className="font-mono-ticker text-2xl font-semibold text-slate-100">{statusCounts[st] || 0}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {st === "open" && "Still being measured"}
                {st === "completed" && "Older than 30d"}
                {st === "invalidated" && "Price broke key level"}
                {st === "replaced" && "Newer signal exists"}
              </div>
            </button>
            </Hint>
          );
        })}
      </div>

      {/* Horizon performance */}
      <div className="text-[11px] text-slate-500 -mb-2" data-testid="signal-stats-label"><strong className="text-slate-300">Signal forward returns</strong> — price change after each signal (not the accuracy score). The EV-IDEX Prediction Accuracy Score, graded from the permanent ledger, is shown above and is identical to the dashboard.</div>
      <HorizonBlock label={horizon.toUpperCase() + " Performance"} s={overall} />

      {/* By strength + By coin */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="apx-card" data-testid="by-strength-card">
          <div className="p-4 border-b border-slate-800 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-cyan-300" />
            <Hint k="by_strength" side="right"><h3 className="font-display font-semibold cursor-help">By Signal Strength (30d)</h3></Hint>
          </div>
          {!stats?.by_strength?.length ? (
            <div className="p-6 text-xs text-slate-500 italic">Not enough aged data yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 border-b border-slate-900">
                  <th className="text-left px-4 py-2">Bucket</th>
                  <th className="text-right px-4 py-2">Signals</th>
                  <th className="text-right px-4 py-2">Win %</th>
                  <th className="text-right px-4 py-2">Avg Return</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_strength.map((b) => (
                  <tr key={b.bucket} className="border-b border-slate-900">
                    <td className="px-4 py-2 font-mono-ticker text-cyan-200">{b.bucket}</td>
                    <td className="px-4 py-2 text-right font-mono-ticker">{b.count}</td>
                    <td className="px-4 py-2 text-right font-mono-ticker text-emerald-400">{b.win_rate}%</td>
                    <td className={`px-4 py-2 text-right font-mono-ticker ${b.avg_return >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {fmtPct(b.avg_return)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="apx-card" data-testid="by-coin-card">
          <div className="p-4 border-b border-slate-800 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            <Hint k="by_coin" side="right"><h3 className="font-display font-semibold cursor-help">Top Performers by Coin (30d)</h3></Hint>
          </div>
          {!stats?.by_coin?.length ? (
            <div className="p-6 text-xs text-slate-500 italic">Not enough aged data yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 border-b border-slate-900">
                  <th className="text-left px-4 py-2">Coin</th>
                  <th className="text-right px-4 py-2">Signals</th>
                  <th className="text-right px-4 py-2">Win %</th>
                  <th className="text-right px-4 py-2">Avg Return</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_coin.slice(0, 10).map((c) => (
                  <tr key={c.coin_id}
                      className="border-b border-slate-900 hover:bg-slate-800/40 cursor-pointer"
                      onClick={() => nav(`/coin/${c.coin_id}`)}>
                    <td className="px-4 py-2 text-cyan-300">{c.name}</td>
                    <td className="px-4 py-2 text-right font-mono-ticker">{c.count}</td>
                    <td className="px-4 py-2 text-right font-mono-ticker text-emerald-400">{c.win_rate}%</td>
                    <td className={`px-4 py-2 text-right font-mono-ticker ${c.avg_return >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {fmtPct(c.avg_return)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Signal history table */}
      <div className="apx-card">
        <div className="p-4 border-b border-slate-800 flex items-center gap-3 flex-wrap">
          <h3 className="font-display font-semibold">Signal History</h3>
          <div className="ml-auto flex gap-2 flex-wrap">
            <select
              data-testid="filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="apx-ring px-3 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs"
            >
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="completed">Completed</option>
              <option value="invalidated">Invalidated</option>
              <option value="replaced">Replaced</option>
            </select>
            <select
              data-testid="filter-outlook"
              value={outlookFilter}
              onChange={(e) => setOutlookFilter(e.target.value)}
              className="apx-ring px-3 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs"
            >
              <option value="all">All outlooks</option>
              <option value="Bullish">Bullish</option>
              <option value="Bearish">Bearish</option>
              <option value="Neutral">Neutral</option>
              <option value="Wait">Wait</option>
            </select>
            <select
              data-testid="filter-coin"
              value={coinFilter}
              onChange={(e) => setCoinFilter(e.target.value)}
              className="apx-ring px-3 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs"
            >
              <option value="all">All coins</option>
              {coinOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        {loading && <div className="p-6 grid gap-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-10" />)}</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="no-signals">
            No signals match these filters. Open any coin and run an analysis to begin building your track record.
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="signals-table">
              <thead>
                <tr className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 border-b border-slate-900">
                  <th className="text-left px-4 py-2">When</th>
                  <th className="text-left px-4 py-2">Asset</th>
                  <th className="text-left px-4 py-2">Outlook</th>
                  <th className="text-left px-4 py-2 hidden sm:table-cell">Status</th>
                  <th className="text-left px-4 py-2"><HintLabel k="momentum_col">Momentum</HintLabel></th>
                  <th className="text-right px-4 py-2"><HintLabel k="strength_col">Strength</HintLabel></th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Risk</th>
                  <th className="text-right px-4 py-2 hidden sm:table-cell">Price Then</th>
                  <th className="text-right px-4 py-2 hidden md:table-cell">Price Now</th>
                  <th className="text-right px-4 py-2"><HintLabel k="delta_since">Δ Since</HintLabel></th>
                  <th className="text-right px-4 py-2"><HintLabel k="outcome">Outcome</HintLabel></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const ol = s.outlook || s.stance || "Neutral";
                  const str = s.signal_strength ?? s.confidence ?? 0;
                  return (
                    <tr key={s.id} data-testid={`signal-${s.id}`}
                        className="border-b border-slate-900 hover:bg-slate-800/30 cursor-pointer"
                        onClick={() => nav(`/coin/${s.coin_id}`)}>
                      <td className="px-4 py-2 text-xs text-slate-400">{new Date(s.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2">
                        <div className="font-medium">{s.name}</div>
                        <div className="text-[10px] font-mono-ticker uppercase text-slate-500">{s.symbol}</div>
                      </td>
                      <td className="px-4 py-2"><OutlookPill outlook={ol} /></td>
                      <td className="px-4 py-2 hidden sm:table-cell"><StatusBadge status={s.status || "open"} /></td>
                      <td className="px-4 py-2"><MomentumCell s={s} /></td>
                      <td className="px-4 py-2 text-right font-mono-ticker">{str}</td>
                      <td className="px-4 py-2 hidden md:table-cell text-xs text-slate-300">{s.risk}</td>
                      <td className="px-4 py-2 text-right font-mono-ticker hidden sm:table-cell">{fmtUsd(s.price)}</td>
                      <td className="px-4 py-2 text-right font-mono-ticker hidden md:table-cell">{fmtUsd(s.current_price)}</td>
                      <td className={`px-4 py-2 text-right font-mono-ticker ${s.price_change_since >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {s.price_change_since !== undefined ? fmtPct(s.price_change_since) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {s.outcome === "correct" ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                        ) : s.outcome === "incorrect" ? (
                          <XCircle className="w-4 h-4 text-red-400 inline" />
                        ) : (
                          <Minus className="w-4 h-4 text-slate-500 inline" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="apx-card p-4 text-xs text-slate-400">
        <strong className="text-amber-300">Note:</strong> Performance stats require signals that are old enough to measure forward returns (7d / 30d / 90d). Fresh signals appear in the table immediately but contribute to performance metrics only after that time has passed. Signal Strength never represents a guaranteed probability of profit.
      </div>
    </div>
  );
}
